/**
 * Denomination splitting for Cashero.
 *
 * Splits an amount into a sum of powers of 2 (the denomination system of Cashu/Cashero).
 * The mint maintains keypairs for each power-of-2 denomination, and each proof
 * corresponds to exactly one denomination amount.
 *
 * For example, 1375 piconero = 1024 + 256 + 64 + 16 + 8 + 4 + 2 + 1
 */

/**
 * Split a total amount into the smallest set of power-of-2 denominations.
 *
 * @param amount - Total amount in piconero
 * @param maxDenom - Maximum denomination allowed (optional, caps the largest power of 2)
 * @returns Array of denomination amounts (powers of 2) that sum to the input
 *
 * @example
 * splitAmount(1375) // returns [1024, 256, 64, 16, 8, 4, 2, 1]
 * splitAmount(10000000000, 2147483648) // uses 2^31 multiple times for large amounts
 */
export function splitAmount(amount: number, maxDenom?: number): number[] {
  if (amount <= 0) return [];
  if (!Number.isInteger(amount)) {
    throw new Error(`Amount must be an integer (piconero), got ${amount}`);
  }

  const denominations: number[] = [];
  let remaining = amount;

  // Determine the starting power
  let maxPower = Math.floor(Math.log2(remaining));
  // Cap at max denomination if provided
  if (maxDenom !== undefined && maxDenom > 0) {
    const maxPowerAllowed = Math.floor(Math.log2(maxDenom));
    maxPower = Math.min(maxPower, maxPowerAllowed);
  }

  while (remaining > 0 && maxPower >= 0) {
    const denom = 2 ** maxPower;
    if (denom <= remaining) {
      // Use as many of this denomination as fit
      const count = Math.floor(remaining / denom);
      for (let i = 0; i < count; i++) {
        denominations.push(denom);
      }
      remaining -= denom * count;
    }
    maxPower--;
  }

  return denominations;
}

/**
 * Split an amount into denominations, but ensure a specific set of output amounts
 * if provided (useful for exact swaps).
 *
 * @param amount - Total amount in piconero
 * @param preferred - Preferred output denominations (optional)
 * @returns Array of denomination amounts
 * @throws If the preferred split doesn't sum to the amount or uses invalid denominations
 */
export function splitAmountWithPreferred(
  amount: number,
  preferred?: number[],
  validDenominations?: number[],
): number[] {
  if (preferred && preferred.length > 0) {
    const sum = preferred.reduce((a, b) => a + b, 0);
    if (sum !== amount) {
      throw new Error(`Preferred split sum (${sum}) doesn't match amount (${amount})`);
    }
    if (validDenominations) {
      for (const d of preferred) {
        if (!validDenominations.includes(d)) {
          throw new Error(`Invalid denomination ${d}: not in keyset`);
        }
      }
    }
    return preferred;
  }

  return splitAmount(amount);
}

/**
 * Get all available denomination amounts from a set of keyset pubkeys.
 */
export function getAvailableDenominations(pubkeys: Record<number, string>): number[] {
  return Object.keys(pubkeys)
    .map(Number)
    .sort((a, b) => a - b);
}

/**
 * Check if a denomination amount exists in a keyset.
 */
export function isValidDenomination(amount: number, pubkeys: Record<number, string>): boolean {
  return amount in pubkeys;
}

/**
 * Select proofs from a wallet's pool to cover a specific amount.
 * Uses the largest-denomination-first algorithm.
 *
 * @param proofs - Available proofs (amount, C, secret, id)
 * @param targetAmount - Amount to cover in piconero
 * @returns Selected proofs and the remaining proofs (change)
 */
export function selectProofsForAmount<T extends { amount: number }>(
  proofs: T[],
  targetAmount: number,
): { selected: T[]; remaining: T[]; totalSelected: number } {
  // Sort proofs by amount descending (largest first)
  const sorted = [...proofs].sort((a, b) => b.amount - a.amount);

  const selected: T[] = [];
  const remaining: T[] = [];
  let selectedSum = 0;

  for (const proof of sorted) {
    if (selectedSum < targetAmount) {
      selected.push(proof);
      selectedSum += proof.amount;
    } else {
      remaining.push(proof);
    }
  }

  // If we couldn't cover the amount, all proofs go back
  if (selectedSum < targetAmount) {
    return { selected: [], remaining: proofs, totalSelected: 0 };
  }

  return { selected, remaining, totalSelected: selectedSum };
}

/**
 * Calculate the change amount if selected proofs exceed the target.
 */
export function calculateChange(selectedAmount: number, targetAmount: number): number {
  if (selectedAmount < targetAmount) return 0;
  return selectedAmount - targetAmount;
}
